import React, {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from "react";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { useDispatch, useSelector } from "react-redux";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import banner1 from "../../assets/images/banner1.jpg";
import { BannerSection } from "../../components/BannerSection/BannerSection";
import { ButtonLink } from "../../components/ButtonLink/ButtonLink"
import { Loader } from "../../components/Loader/Loader"
import { ReviewSection } from "../../components/ReviewSection/ReviewSection";
import { AppDispatch, RootState } from "../../store";
import { getCard } from "../../store/slices/cardSlice";
import {
  fetchCategories,
  selectCategories,
} from "../../store/slices/categoriesSlice";
import { getProductCategories } from "../../store/slices/productCategoriesSlice";
import {
  fetchReviews,
  selectReviews,
} from "../../store/slices/reviewsSlice";
import {
  Section,
  SmallTitle,
  TitleH2,
} from "../../styles/sharedStyles"

import { AnimatedBlock } from "./AnimatedBlock/AnimatedBlock";
import { CustomCard } from "./CustomCard/CustomCard";
import {
  Container,
  CustomParagraph,
  Paragraph,
  TabButton,
  TabButtons,
  TabContent,
  TitleH1,
} from "./styles";
import { TariffCard } from "./TariffCard/TariffCard";

const productTabMap: Record<"consulting" | "job", number[]> = {
  consulting: [5, 28, 11, 13], // Психология и консалтинг
  job: [15, 9, 12, 14, 16, 17], // Поиск работы
};

export const Main = () => {

  const [activeTab, setActiveTab] = useState<"consulting" | "job">("consulting");

  const dispatch: AppDispatch = useDispatch<AppDispatch>();
  const reviews = useSelector(selectReviews);
  const categories = useSelector(selectCategories);

  useEffect(() => {
    dispatch(getCard(8));
  }, [dispatch]);

  useEffect(() => {
    if (categories.length === 0) {
      dispatch(getProductCategories());
    }
  }, [dispatch, categories.length]);

  useEffect(() => {
    if (reviews.length === 0) {
      dispatch(fetchReviews());
    }
    if (categories.length === 0) {
      dispatch(fetchCategories());
    }
  }, [dispatch, reviews.length, categories.length]);

  const card = useSelector((state: RootState) => state.card.card);

  const [ strMaxLength, filteredProducts] = useMemo(() => {
    const ids = productTabMap[activeTab] || [];

    const cardSelected = ids
      .map((id) => card.find((item) => item.id === id))
      .filter(Boolean);

    const max = Math.max(...cardSelected.map(
        (c)=>String(c.max_price).length + String(c.min_price).length
      )
    )

    return [max, cardSelected]
  }, [card, activeTab]);

  return (
    <Fragment>
      <Section>
        <TitleH1>
          Выведем карьеру<br/>
          <span>на новый уровень</span>
        </TitleH1>
        <Paragraph>
          Развитие карьеры и психологическая
          поддержка для специалистов любого уровня
          по всему миру.
        </Paragraph>
      </Section>
      <BannerSection
        hasTypeWriter
        hasSmallTitle
        backgroundImage={banner1}
        smallTitle="мы помогаем"
        buttonText="Наши услуги"
        buttonLink="/services"
      />
      <Section>
        <Container>
          <SmallTitle>тарифы</SmallTitle>
          <TitleH2>Как мы можем помочь тебе?</TitleH2>
          <TabButtons>
            <TabButton
              isActive={activeTab === "consulting"}
              onClick={() => setActiveTab("consulting")}
            >Психология и консалтинг
            </TabButton>
            <TabButton
              isActive={activeTab === "job"}
              onClick={() => setActiveTab("job")}
            >Поиск работы
            </TabButton>
          </TabButtons>
          <TabContent>
            {Array.isArray(filteredProducts) && filteredProducts.length > 0 ? (
              <Swiper
                modules={[Pagination, Navigation]}
                pagination={{ dynamicBullets: true }}
                spaceBetween={24}
                slidesPerView={"auto"}
              >
                {filteredProducts.map((product) => (
                  <SwiperSlide key={product.id} style={{ width: 312 }}>
                    <TariffCard
                      category={product.product_category?.title || "Без категории"}
                      categoryId={product.product_category?.id || 0}
                      title={product.title || "Без названия"}
                      description={product.description || ""}
                      duration={product.duration_text || ""}
                      price={product.price || 0}
                      min_price={product.min_price || 0}
                      max_price={product.max_price || 0}
                      tag={product.tag || ""}
                      tabType={activeTab}
                      strMaxLength={strMaxLength}
                    />
                  </SwiperSlide>
                ))}
                {activeTab === "job" && (
                  <SwiperSlide style={{ width: 312 }}>
                    <CustomCard tabType="job"/>
                  </SwiperSlide>
                )}
              </Swiper>
            ) : (
              <div style={{ width: "100%" }}>
                <Loader/>
              </div>
            )}
          </TabContent>
        </Container>
      </Section>
      <Section>
        <Container>
          <SmallTitle>миссия</SmallTitle>
          <TitleH2>Прохорова Консалт <span>– команда<br/>
            профессионалов
          </span>
          </TitleH2>
          <CustomParagraph>
            Мы даем бутиковый сервис развития карьеры для тех, кто ценит свой ресурс <br/>
            и стремится получать больший результат от меньших усилий.
          </CustomParagraph>
          <ButtonLink
            toPage="/blog"
            title="Наши кейсы"
          />
        </Container>
      </Section>
      <Section>
        <Container>
          <TitleH2>Направления деятельности</TitleH2>
          <AnimatedBlock  />
        </Container>
      </Section>
      <BannerSection
        hasOuterLink
        hasDecorText
        hasSmallTitle
        backgroundImage={banner1}
        smallTitle="Подход"
        buttonText="Получить консультацию"
        buttonLink="https://t.me/ProhorovaConsult"
        mainText="Подготовьтесь к успеху c персональной карьерной стратегией"
      />
      <Section>
        <Container>
          <TitleH2>Отзывы</TitleH2>
          <ReviewSection
            reviews={reviews}
            categories={categories}
          />
          <ButtonLink
            toPage="/reviews"
            title="Все отзывы"
          />
        </Container>
      </Section>
    </Fragment>
  );
};

